import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { message, Modal } from "antd";
import { ContentTopic } from "api/config/chalice-types";
import { genericError } from "fixtures/globalConstants";
import { useCallback, useState } from "react";
import { updateBusinessDetails } from "store/business/businessActions";
import { trackEvent } from "utils/eventTracking/trackEvents";

interface UseBulkEditProps {
  displayTopics: ContentTopic[];
  dispatch: ThunkDispatch<any, undefined, AnyAction>; // Type for Redux dispatch
  currentBusinessId: string | undefined;
  canDeleteTopics: boolean;
  generateTopicKey: (topic: ContentTopic) => string;
}

interface UseBulkEditReturn {
  isBulkEditMode: boolean;
  selectedTopicKeys: Set<string>;
  handleToggleBulkEditMode: () => void;
  handleTopicSelect: (topicKey: string) => void;
  handleBulkDelete: () => void;
}

export const useBulkEdit = ({
  displayTopics,
  dispatch,
  currentBusinessId,
  canDeleteTopics,
  generateTopicKey,
}: UseBulkEditProps): UseBulkEditReturn => {
  const [isBulkEditMode, setIsBulkEditMode] = useState<boolean>(false);
  const [selectedTopicKeys, setSelectedTopicKeys] = useState<Set<string>>(
    new Set()
  );

  const handleToggleBulkEditMode = useCallback(() => {
    setIsBulkEditMode((prev) => !prev);
    setSelectedTopicKeys(new Set()); // Clear selection when toggling mode
  }, []);

  const handleTopicSelect = useCallback((topicKey: string) => {
    setSelectedTopicKeys((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(topicKey)) {
        newSelection.delete(topicKey);
      } else {
        newSelection.add(topicKey);
      }
      return newSelection;
    });
  }, []);

  // Moved bulk delete logic here
  const handleBulkDelete = useCallback(() => {
    if (!canDeleteTopics) {
      message.error("You do not have permission to delete topics.");
      return;
    }
    if (selectedTopicKeys.size === 0) {
      message.info("No topics selected.");
      return;
    }

    const originalTopics = [...displayTopics];
    const currentEnabledCount = originalTopics.filter((t) => t.enabled).length;
    const selectedEnabledCount = originalTopics.filter(
      (t) => t.enabled && selectedTopicKeys.has(generateTopicKey(t))
    ).length;

    if (
      selectedEnabledCount >= currentEnabledCount &&
      selectedEnabledCount > 0
    ) {
      message.warning(
        "Cannot delete all enabled topics. At least one must remain enabled."
      );
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedTopicKeys.size} Topics?`,
      content:
        "Are you sure you want to permanently delete the selected topics? This action cannot be undone.",
      okText: "Delete Selected Topics",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const newTopics = originalTopics.filter(
          (topic) => !selectedTopicKeys.has(generateTopicKey(topic))
        );

        if (!currentBusinessId) {
          message.error("Business ID not found. Cannot save changes.");
          // Optionally revert UI or handle differently?
          // For now, just show error and don't proceed with UI change.
          return;
        }

        try {
          // Use the correctly imported named export
          // @ts-ignore - Preserve existing ignore for updateBusinessDetails
          await dispatch(
            updateBusinessDetails({ topics: newTopics } as any)
          ).unwrap();

          trackEvent("updated_topics", { action: "delete" });
          message.success(
            `${selectedTopicKeys.size} topics deleted successfully.`
          );
          // Exit bulk edit mode and clear selection *after* successful deletion
          handleToggleBulkEditMode(); // Use the hook's own toggler
        } catch (error) {
          // Backend update failed
          genericError(error); // Show generic error
          message.error("Failed to delete selected topics.");
          // Parent component's displayTopics won't be updated due to error.
          // No need to revert state managed within this hook.
        }
      },
      onCancel: () => {},
    });
  }, [
    canDeleteTopics,
    selectedTopicKeys,
    displayTopics,
    generateTopicKey,
    dispatch,
    currentBusinessId,
    handleToggleBulkEditMode, // Add dependency
  ]);

  return {
    isBulkEditMode,
    selectedTopicKeys,
    handleToggleBulkEditMode,
    handleTopicSelect,
    handleBulkDelete,
  };
};
